import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserService } from '../service/user.service';
import { NavigationConfigService } from '../service/navigation-config.service';
import { TokenRefreshService } from '../service/token-refresh.service';

// Public route segments that must stay reachable without an authenticated session.
// Mirrors the exclusion list previously used by LayoutComponent's NavigationEnd check.
const PUBLIC_SEGMENTS = new Set(['Login', 'ForgetPassword', 'SSO', 'Auth', 'Error']);

// The production host has no in-app /Login route — authentication lives on the
// external login site (same target LayoutComponent's logout uses when live). Detect it
// by hostname rather than environment.isLogoOutFromLive, which isn't reliably present
// in every build. Any other host (localhost / UAT) uses the in-app /Login route.
const LIVE_HOST = 'labour.teamleaseregtech.com';
const LIVE_LOGIN_URL = 'https://login.teamleaseregtech.com/logout.aspx';

/**
 * Function-level authorization run BEFORE a restricted component is activated.
 *
 * The old design let the router render the lazy component first and only checked the
 * role afterwards (LayoutComponent, on NavigationEnd) — so a restricted page painted
 * briefly and then bounced to /Error/403. Running the same check as a CanActivate guard
 * means the target component is never instantiated when access is denied, and the check
 * re-runs on every navigation (refresh, deep-link, back/forward), so none of those can
 * bypass it.
 *
 * The allowed-route list is no longer a static client-side table (the old
 * src/data/ValidRouts.ts shipped every role's permission matrix in the JS bundle —
 * VAPT finding). It now comes from /api/MenuHandler/GetNavigationConfig, which derives
 * role and SPID from the caller's JWT claims, so the client only ever holds its own
 * role's slice. NavigationConfigService caches it per session, so this resolves
 * synchronously after the first navigation. If the config cannot be fetched, access
 * is denied (deny-by-default).
 *
 * NOTE: this is a client-side control only. The authoritative fix for an authenticated
 * lower-privilege token is server-side, role-based, deny-by-default authorization on the
 * backing endpoints — see the API team's remediation.
 */
export const authRouteGuard: CanActivateFn = (_route, state) => {
  const userService = inject(UserService);
  const navConfigService = inject(NavigationConfigService);
  const tokenRefreshService = inject(TokenRefreshService);
  const router = inject(Router);

  const currentRoute = state.url.split('?')[0].split('/')[1] ?? '';

  // Public routes are always allowed (login / auth-redirect / error pages).
  if (!currentRoute || PUBLIC_SEGMENTS.has(currentRoute)) {
    return true;
  }

  const role = userService.getRole();

  // The ACCESS TOKEN is checked alongside the role: GetNavigationConfig is an
  // [Authorize] endpoint, so calling it before the token has been stored sends an empty
  // Bearer, gets a 401, and the auth interceptor then tries to refresh — with no refresh
  // token either, that ends in forceLogout(), which clears sessionStorage and bounces the
  // user to /Login. A session is only usable when BOTH are present, so treat a missing
  // token exactly like a missing role and never reach the config call without one.
  const token = userService.getAccessToken();

  // No authenticated session in this tab (e.g. a fresh-tab deep-link has empty
  // sessionStorage). On the live host there is no in-app /Login route — send the
  // visitor to the external login site; everywhere else go to the in-app /Login,
  // before the restricted component is instantiated.
  if (!role || !token) {
    if (window.location.hostname === LIVE_HOST) {
      window.location.href = LIVE_LOGIN_URL;
      return false;
    }
    return router.createUrlTree(['/Login']);
  }

  // The server already applied the role + SPID matching rules; the client only
  // checks membership in its own slice.
  return navConfigService.getConfig().pipe(
    map(config => config.routes.includes(currentRoute)
      ? true
      : denyOrReturnFromImpersonation(state.url, tokenRefreshService, router)),
    catchError(() => of(router.createUrlTree(['/Error', '403'])))
  );
};

/**
 * Denied for the CURRENT role — but that role may be an impersonated one.
 *
 * A performer/admin can sign in to a customer's client portal (ClientPortalLogin →
 * UserService.backupSession), which swaps the session for the client's HMGR/HAPPR identity
 * and stashes the original under 'userDataBackup'. Navigating BACK to an internal screen
 * such as /EntityDashboard is then evaluated against the CLIENT role, which of course has
 * no access — so the user was bounced to /Error/403.
 *
 * That return trip used to work because authorization ran after the component rendered, so
 * EntitydashboardComponent.ngOnInit got to restore the original session first. With the
 * check moved into this CanActivate guard the component is never created, and the restore
 * never happens — the user is stranded in the impersonated session.
 *
 * So when a backup exists, treat the denial as "they are on their way back": restore the
 * original session and hard-navigate to the same URL, where the guard re-runs against the
 * restored role and allows or denies it on its own merits. No privilege is granted here —
 * this only returns the user to the session they already had, and every request is still
 * authorised server-side against the restored token.
 */
function denyOrReturnFromImpersonation(
  targetUrl: string,
  tokenRefreshService: TokenRefreshService,
  router: Router
): boolean | UrlTree {
  if (sessionStorage.getItem('userDataBackup')) {
    tokenRefreshService.restoreBackupSessionAndReload(targetUrl);
    return false; // cancel this navigation; the document reload takes over
  }
  return router.createUrlTree(['/Error', '403']);
}
